import { IAccountRespSchema } from '@monetize/types/app';
import { FilterTypeOperator } from '@monetize/types/common';
import { useCallback, useMemo, useRef } from 'react';
import { Link as ReactDomLink } from 'react-router-dom';
import {
  MBox,
  MButton,
  MFlex,
  MHStack,
  MPageHeader,
  MPageSearchInput,
} from '../../../components/Monetize';
import { useTableColumns } from '../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import { getAccountQuoteNewRoute } from '../../../constants/routes';
import { useQuotesTableData } from '../../../hooks/AccountDetail';
import {
  combineFilterStateKey,
  FilterStateKeys,
} from '../../../store/store.types';
import { getIsTrulyEmptyList } from '../../../utils';
import { useCanCreateQuote } from '../../Quotes/Quote/useCanCreateQuote';
import {
  QUOTE_LIST_CATALOG,
  QuoteListColumnContext,
} from './quoteList.columns';

const QuoteListAccount = ({
  account,
  billGroupId,
}: {
  account: IAccountRespSchema;
  billGroupId?: string;
}) => {
  const { id: accountId = '' } = account;
  const {
    filters,
    setFilters,
    searchKey,
    searchTerm,
    setSearchTerm,
    onResetFilter,
    quoteList,
    pager,
    setPager,
    loading,
    actionBodyTemplate,
    handleReviewQuote,
    handleEditQuote,
    refetch,
  } = useQuotesTableData({
    accountId,
    billGroupId,
    filterKey: combineFilterStateKey(FilterStateKeys.QUOTE_LIST, { accountId }),
  });

  const canCreateQuote = useCanCreateQuote();

  const tableRef = useRef<DataTableRef>(null);

  const onResetFilters = useCallback(() => {
    onResetFilter();
    tableRef.current?.resetFilters();
  }, [onResetFilter]);

  const columnContext = useMemo<QuoteListColumnContext>(
    () => ({ actionBodyTemplate }),
    [actionBodyTemplate],
  );
  const { columns, state: columnState } = useTableColumns(
    QUOTE_LIST_CATALOG,
    columnContext,
  );

  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading,
    totalElements: quoteList?.totalElements || 0,
    filters,
    searchTerm,
    page: pager.page,
  });

  return (
    <MBox>
      <MPageHeader title="Quotes" hideContent={isTrulyEmptyList}>
        <MFlex>
          <MHStack gap="2">
            <MPageSearchInput
              placeholderKey="Quote Name"
              value={searchTerm}
              count={quoteList?.totalElements}
              mode="alwaysExpanded"
              onChange={setSearchTerm}
            />
            <RefreshTableButton refetch={refetch} />
            <ExportEntityButton
              entity="quotes"
              filters={filters}
              internalFilters={[
                {
                  key: 'accountId',
                  value: accountId,
                  operator: FilterTypeOperator.EQUAL,
                },
              ]}
              applyInternalFiltersWithoutConfirmation
              getFilename={() => `quotes-${accountId}.csv`}
              searchKey={searchKey}
              searchTerm={searchTerm}
              sortField={pager.sortField}
              sortOrder={pager.sortOrder}
              endpointParams={[accountId]}
            />
            {canCreateQuote && (
              <MButton variant="secondary" asChild>
                <ReactDomLink to={getAccountQuoteNewRoute(accountId)}>
                  New Quote
                </ReactDomLink>
              </MButton>
            )}
          </MHStack>
        </MFlex>
      </MPageHeader>

      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={quoteList?.content ?? []}
        isLoading={loading}
        enablePagination
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={quoteList?.totalElements}
        totalPages={quoteList?.totalPages}
        pager={pager}
        setPager={setPager}
        initialFilters={filters}
        onFiltersChange={(newFilters) => setFilters(newFilters)}
        onRowClick={(row, _tableRow, openInNewWindow) => {
          if (row.status === 'DRAFT') {
            handleEditQuote(row.id, openInNewWindow);
          } else {
            handleReviewQuote(row.id, openInNewWindow);
          }
        }}
        emptyProps={{
          mainMessage: 'Looks like there are no quotes here.',
          btnLabel: canCreateQuote ? 'New Quote' : null,
          to: getAccountQuoteNewRoute(accountId),
        }}
        emptyFilterResultsProps={{ btnLabel: 'Reset Filters' }}
        resetFilter={onResetFilters}
      />
    </MBox>
  );
};

export default QuoteListAccount;
